import React, { useEffect, useState } from "react";
import { Handle, NodeResizeControl, Position } from "@xyflow/react";
import { NodeIcon } from "../components/Workflows/NodeIcon";
import { DatasetPicker, ModelPicker } from "../components/Workflows/NodePickers";
import { ClientKeyOptionsPicker, RemoteSelectControl } from "../components/Workflows/NodeOptionPickers";
import { NodeBuilderPanel } from "../components/Workflows/NodeBuilderPanel";
import { capabilityMap } from "../utils/nodeBuilder";
import { useNodeCapabilities } from "../hooks/useNodeCapabilities";
import {
  flatPatch,
  growInputs,
  renameInput,
  resolveHandles,
  COMPACT_NODE_SIZE,
  defaultInputsFor,
  isCompactNode,
  isParameterVisible,
  resolveInputArray,
  resolveParameterValue,
} from "../utils/dynamicNodeData";

// A node rendered purely from the server's node definition, using the SAME flat `data` model
// as the prebuilt nodes: values live directly on `data` (data.model, data.inputs, data.code,
// …), never in a nested structure. Swapping a prebuilt node for this renderer therefore keeps
// the graph definition identical.

const CATEGORY_COLORS = {
  entry: "#2563eb",
  output: "#475569",
  ai: "#7c3aed",
  retrieval: "#0891b2",
  data: "#0f766e",
  transform: "#ea580c",
  routing: "#ca8a04",
  integration: "#db2777",
  ingestion: "#059669",
  workflow: "#4f46e5",
};

const fieldStyle = {
  width: "100%",
  padding: "5px 8px",
  fontSize: "12px",
  fontFamily: "inherit",
  color: "var(--main-font-color)",
  backgroundColor: "var(--main-bg)",
  border: "1px solid var(--main-border-color)",
  borderRadius: "5px",
  boxSizing: "border-box",
};

const monoField = { ...fieldStyle, fontFamily: "monospace" };

const jsonString = (value) => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value ?? "");
  }
};

const toText = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((item) => toText(item)).filter(Boolean).join(", ");
  return String(value);
};

/** Best-guess the number of rows a text area should take from the parameter itself. */
const guessTextareaRows = (parameter) => {
  const control = parameter?.control ?? "text";
  const value = parameter?.default;

  if (typeof value === "string") {
    const lines = value.split("\n").length;
    if (lines > 1) return Math.min(12, lines + 1);
  }
  if (Array.isArray(value)) return Math.min(8, Math.max(3, value.length + 1));

  if (control === "code") return parameter?.language === "sql" ? 4 : 6;
  if (control === "json") return 4;

  const descriptionLength = String(parameter?.description ?? "").length;
  const placeholderLength = String(parameter?.placeholder ?? "").length;
  if (descriptionLength > 80 || placeholderLength > 80) return 4;

  return 3;
};

const iconButtonStyle = {
  flexShrink: 0,
  width: "22px",
  height: "22px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--main-border-color)",
  borderRadius: "4px",
  background: "transparent",
  color: "var(--main-font-color)",
  cursor: "pointer",
  fontSize: "14px",
  lineHeight: 1,
};

const addButtonStyle = {
  alignSelf: "flex-start",
  padding: "1px 8px",
  fontSize: "11px",
  fontWeight: 600,
  border: "1px dashed var(--main-border-color)",
  borderRadius: "5px",
  background: "transparent",
  color: "var(--main-font-color)",
  cursor: "pointer",
};

const removeButtonStyle = {
  alignSelf: "flex-start",
  padding: "1px 8px",
  fontSize: "11px",
  fontWeight: 600,
  border: "1px solid var(--main-border-color)",
  borderRadius: "5px",
  background: "transparent",
  color: "var(--main-font-color)",
  cursor: "pointer",
};

const isLongTextField = (name) => /criteria|description|prompt|instruction|content/i.test(String(name ?? ""));

/**
 * Guess the fields of a `list[object]` entry from the first item, the placeholder's example
 * object, or the `{a, b}` shape in the description — so objects never have to be edited as JSON.
 */
const deriveObjectFields = (value, placeholder, description) => {
  const list = Array.isArray(value) ? value : [];
  const first = list.find((item) => item && typeof item === "object" && !Array.isArray(item));
  if (first) return Object.keys(first);

  if (placeholder) {
    try {
      const parsed = JSON.parse(placeholder);
      const firstParsed = Array.isArray(parsed)
        ? parsed.find((item) => item && typeof item === "object" && !Array.isArray(item))
        : null;
      if (firstParsed) return Object.keys(firstParsed);
    } catch (error) {
      // Not JSON — fall through to the description pattern.
    }
  }

  const brace = String(description ?? placeholder ?? "").match(/\{([^}]+)\}/);
  if (brace) {
    const fields = brace[1].split(",").map((field) => field.trim()).filter(Boolean);
    if (fields.length) return fields;
  }

  return ["label", "value"];
};

/** A list of strings edited one entry per row — never raw JSON. */
const StringListEditor = ({ value, onChange }) => {
  const list = Array.isArray(value) ? value : [];

  const update = (index, text) => onChange(list.map((item, i) => (i === index ? text : item)));
  const add = () => onChange([...list, ""]);
  const remove = (index) => onChange(list.filter((_, i) => i !== index));

  return (
    <div className="nodrag" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {list.map((item, index) => (
        <div key={index} style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <input
            type="text"
            style={fieldStyle}
            value={String(item ?? "")}
            onChange={(event) => update(index, event.target.value)}
          />
          <button type="button" style={iconButtonStyle} aria-label="Remove item" onClick={() => remove(index)}>×</button>
        </div>
      ))}
      <button type="button" style={addButtonStyle} onClick={add}>+ Add item</button>
    </div>
  );
};

/** A list of objects edited field-by-field — never raw JSON. */
const ObjectListEditor = ({ value, onChange, placeholder, description }) => {
  const list = Array.isArray(value) ? value : [];
  const fields = deriveObjectFields(value, placeholder, description);

  const update = (index, field, fieldValue) =>
    onChange(list.map((item, i) => (i === index ? { ...(item ?? {}), [field]: fieldValue } : item)));
  const add = () => onChange([...list, Object.fromEntries(fields.map((field) => [field, ""]))]);
  const remove = (index) => onChange(list.filter((_, i) => i !== index));

  return (
    <div className="nodrag" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {list.map((item, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            padding: "6px",
            border: "1px solid var(--content-item-border-color)",
            borderRadius: "5px",
          }}
        >
          {fields.map((field) => (
            <div key={field} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "10.5px", fontWeight: 600 }}>{field}</span>
              {isLongTextField(field) ? (
                <textarea
                  className="nowheel"
                  rows={2}
                  style={{ ...fieldStyle, resize: "vertical" }}
                  value={String(item?.[field] ?? "")}
                  onChange={(event) => update(index, field, event.target.value)}
                />
              ) : (
                <input
                  type="text"
                  style={fieldStyle}
                  value={String(item?.[field] ?? "")}
                  onChange={(event) => update(index, field, event.target.value)}
                />
              )}
            </div>
          ))}
          <button type="button" style={removeButtonStyle} onClick={() => remove(index)}>Remove</button>
        </div>
      ))}
      <button type="button" style={addButtonStyle} onClick={add}>+ Add item</button>
    </div>
  );
};

/**
 * A select that reflects the chosen option immediately.
 *
 * The node's value round-trips through the graph (data → parent → prop), which lands a render
 * after the change; reading straight from the prop made the dropdown snap back in the meantime.
 * Keeping a local mirror means the choice sticks the moment it's made, and the mirror re-syncs
 * if the value changes elsewhere.
 */
const SelectControl = ({ parameter, value, onChange, disabledValues = [] }) => {
  const options = (parameter?.enum ?? []).map((option) => String(option));
  const normalized = value === undefined || value === null ? "" : String(value);
  const [selected, setSelected] = useState(normalized);

  useEffect(() => {
    setSelected(normalized);
  }, [normalized]);

  const known = selected === "" || options.includes(selected);

  return (
    <select
      className="nodrag"
      style={fieldStyle}
      value={selected}
      onChange={(event) => {
        setSelected(event.target.value);
        onChange(event.target.value);
      }}
    >
      {!known ? <option value={selected}>{selected}</option> : null}
      {selected === "" ? <option value="">(not set)</option> : null}
      {options.map((option) => {
        const disabled = disabledValues.includes(option);
        return (
          <option key={option} value={option} disabled={disabled}>
            {option}{disabled ? " (unavailable)" : ""}
          </option>
        );
      })}
    </select>
  );
};

// Parameters that name a stored client API key get a key picker even when the catalog only
// marks them as text — this covers restRequestNode's auth.clientKey / usernameClientKey /
// passwordClientKey fields.
const CLIENT_KEY_PARAMETERS = ["clientKey", "usernameClientKey", "passwordClientKey"];
const CLIENT_KEY_CONTROLS = ["client-key", "clientkey", "client-key-picker", "clientapikey", "client-api-key"];

const normalizeControl = (parameter) => {
  const raw = String(parameter?.control ?? "").toLowerCase();

  if (CLIENT_KEY_CONTROLS.includes(raw)) return "client-key-picker";
  if ((!raw || raw === "text" || raw === "string") && CLIENT_KEY_PARAMETERS.includes(parameter?.name)) {
    return "client-key-picker";
  }

  return parameter?.control ?? "text";
};

/** A live, controlled control for one parameter, chosen by its `control` hint. */
const Control = ({ parameter, value, onChange, data = {}, capabilities = null, optionsEndpoint = null }) => {
  const control = normalizeControl(parameter);
  const name = parameter?.name ?? "parameter";
  const placeholder = parameter?.placeholder;
  const stringValue = value === undefined || value === null ? "" : String(value);

  switch (control) {
    case "switch":
    case "boolean":
      return (
        <label className="nodrag" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", cursor: "pointer" }}>
          <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
          {Boolean(value) ? "on" : "off"}
        </label>
      );

    case "select": {
      // The `operation` select reflects the connection's per-operation capabilities.
      const blocked =
        parameter?.name === "operation"
          ? Object.entries(capabilityMap(capabilities))
              .filter(([, ok]) => ok === false)
              .map(([name]) => name)
          : [];

      return (
        <SelectControl
          parameter={parameter}
          value={value}
          onChange={onChange}
          disabledValues={blocked}
        />
      );
    }

    case "number":
    case "integer":
      return (
        <input
          className="nodrag"
          type="number"
          style={fieldStyle}
          value={stringValue}
          min={parameter.min}
          max={parameter.max}
          step={parameter.step}
          placeholder={placeholder ?? "not set"}
          onChange={(event) => onChange(event.target.value === "" ? undefined : Number(event.target.value))}
        />
      );

    case "textarea":
      return (
        <textarea
          className="nodrag nowheel"
          rows={guessTextareaRows(parameter)}
          style={{ ...fieldStyle, resize: "vertical" }}
          value={stringValue}
          placeholder={placeholder ?? "not set"}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case "code": {
      const rows = guessTextareaRows(parameter);
      return (
        <div className="nodrag" style={{ position: "relative" }}>
          {parameter?.language ? (
            <span
              style={{
                position: "absolute",
                top: "4px",
                right: "6px",
                fontSize: "9px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "#a0a6b1",
              }}
            >
              {parameter.language}
            </span>
          ) : null}
          <textarea
            className="nowheel"
            rows={rows}
            spellCheck={false}
            style={{ ...monoField, resize: "vertical" }}
            value={stringValue}
            placeholder={placeholder ?? "not set"}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );
    }

    case "json": {
      const text = value === undefined || value === null ? "" : jsonString(value);
      return (
        <textarea
          className="nodrag nowheel"
          rows={guessTextareaRows(parameter)}
          spellCheck={false}
          style={{ ...monoField, resize: "vertical" }}
          value={text}
          placeholder={placeholder ?? "{}"}
          onChange={(event) => {
            const raw = event.target.value;
            try {
              onChange(JSON.parse(raw));
            } catch (error) {
              onChange(raw);
            }
          }}
        />
      );
    }

    case "string-list":
      return <StringListEditor value={value} onChange={onChange} />;

    case "object-list":
      return (
        <ObjectListEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          description={parameter?.description}
        />
      );

    case "url":
      return (
        <input
          className="nodrag"
          type="text"
          style={fieldStyle}
          value={stringValue}
          placeholder={placeholder ?? "https://…"}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case "model-picker":
      return <ModelPicker value={value} onChange={onChange} placeholder={placeholder} />;

    case "dataset-picker":
      return <DatasetPicker value={value} onChange={onChange} placeholder={placeholder} />;

    case "client-key-picker":
      return (
        <ClientKeyOptionsPicker
          parameter={parameter}
          value={value}
          onChange={onChange}
          optionsEndpoint={optionsEndpoint}
        />
      );

    case "remote-select":
      return (
        <RemoteSelectControl
          parameter={parameter}
          data={data}
          value={value}
          onChange={onChange}
          capabilities={capabilities}
          optionsEndpoint={optionsEndpoint}
        />
      );

    // workflow-picker and plain text.
    default:
      return (
        <input
          className="nodrag"
          type="text"
          style={fieldStyle}
          value={stringValue}
          placeholder={placeholder ?? (control.endsWith("picker") ? `<${name}>` : "not set")}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
};

const ParameterField = ({ parameter, value, onChange, data = {}, capabilities = null, optionsEndpoint = null }) => {
  const name = parameter?.name ?? "parameter";
  const type = String(parameter?.type ?? "string");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "5px", fontSize: "12px", fontWeight: 600 }}>
        {name}
        {parameter?.required ? <span title="required" style={{ color: "#c0392b" }}>*</span> : null}
        <span style={{ fontWeight: 400, fontSize: "10.5px", color: "#a0a6b1" }}>{type}</span>
      </div>
      <Control parameter={parameter} value={value} onChange={onChange} data={data} capabilities={capabilities} optionsEndpoint={optionsEndpoint} />
      {parameter?.description ? (
        <div style={{ fontSize: "10.5px", lineHeight: 1.35, color: "#a0a6b1" }}>{parameter.description}</div>
      ) : null}
    </div>
  );
};

/**
 * React Flow node type that draws and edits a server-defined node.
 *
 * @param {object} props
 * @param {string} props.id Node id.
 * @param {object} props.data The node's FLAT data (same keys as the prebuilt nodes).
 * @param {object} [props.definition] The node definition (parameters/handles/defaults/icon).
 *   Defaults to `data` for the standalone preview, where the definition is embedded. In the
 *   editor this is supplied from a registry and is never persisted into the graph.
 */
export const DynamicNode = ({ id, data, definition, height }) => {
  const def = definition ?? data ?? {};
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Flat data model — values live directly on `data`, exactly like the prebuilt nodes.
  const getValue = (parameter) =>
    resolveParameterValue({ parameter, data, defaultData: def.defaultData });
  const setValue = (name, value) => data.onUpdateData?.(id, flatPatch(name, value));

  const parameters = def.parameters ?? [];

  // The catalog may name the options endpoint once on the node (`builder.options_endpoint`)
  // instead of repeating it in every control.
  const optionsEndpoint = def.builder?.options_endpoint ?? null;

  // The values a parameter renders with: node data, then catalog defaults, then the parameter's
  // own default. Visibility checks, dependent pickers and the builder panel all use this, so a
  // defaulted value (e.g. `operation` / `clientKey`) counts as selected.
  const resolvedData = React.useMemo(() => {
    const source = definition ?? data ?? {};
    const parameterDefaults = {};

    (source.parameters ?? []).forEach((parameter) => {
      if (parameter?.name && parameter.default !== undefined) parameterDefaults[parameter.name] = parameter.default;
    });

    return { ...parameterDefaults, ...(source.defaultData ?? {}), ...data };
  }, [definition, data]);

  // Builder capabilities (Slack scopes) are fetched once per node and shared by the controls,
  // the pickers and the builder panel.
  const {
    capabilities,
    loading: capabilitiesLoading,
    error: capabilitiesError,
  } = useNodeCapabilities({ definition: def, data });

  // Nodes with variable inputs (llmNode, pythonNode) get a count + labelled inputs editor.
  const hasVariableInputs = Boolean(def.handles?.dynamicInputs) && Boolean(def.handles?.inputSource);
  const inputSource = def.handles?.inputSource;

  // Every node may have zero inputs — it still runs and produces its outputs — so the count
  // field has no lower floor.
  const minInputs = 0;

  // Explicit inputs win (including []); otherwise match the prebuilt node's default — the LLM
  // node shows none, the Python node shows its single synthesised `input-1`.
  const inputsValue = hasVariableInputs
    ? resolveInputArray({ data, inputSource, defaultInputs: defaultInputsFor(def) })
    : [];

  // `visible_when` is `{field: value}` or `{field: [values]}`; fields whose condition isn't met
  // are hidden (e.g. Slack's per-operation fields).
  const isParameterShown = (parameter) => isParameterVisible(parameter, resolvedData, def.defaultData);

  const renderedParameters = parameters.filter((parameter) => {
    const name = parameter?.name;
    if (name === "label") return false;
    if (hasVariableInputs && (name === inputSource || name === "inputCount")) return false;
    return isParameterShown(parameter);
  });

  const basic = renderedParameters.filter((parameter) => !parameter?.advanced);
  const advanced = renderedParameters.filter((parameter) => parameter?.advanced);

  const setInputCount = (count) => setValue(inputSource, growInputs(inputsValue, count));
  const setInputLabel = (index, label) => setValue(inputSource, renameInput(inputsValue, index, label));

  const { targets, sources } = resolveHandles(def, data);
  const color = def.categoryColor ?? CATEGORY_COLORS[def.category] ?? "var(--button-bg)";

  const label = data.label ?? def.name ?? def.type ?? "Node";
  const description = toText(def.description);

  // Handles sit where the prebuilt nodes put them: node-centred (React Flow's default) so a
  // single handle lines up with the original graph, spread evenly when a node exposes several.
  // Variable-input nodes keep row-anchored handles, like the prebuilt LLM/Python nodes.
  const fixedHeight = typeof height === "number" && height > 0;
  const handleBaseStyle = { height: "8px", width: "8px", borderRadius: "2px", zIndex: 10 };
  const rowHandleStyle = { ...handleBaseStyle, top: "50%" };
  const staticTargets = hasVariableInputs ? [] : targets;
  const spreadTop = (index, count) => `${((index + 1) / (count + 1)) * 100}%`;
  const compact = isCompactNode(def);

  // Entry/output nodes are simple passthroughs: a small square card with their icon and label,
  // matching the compact prebuilt Input/Text Output nodes.
  if (compact) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
          width: "100%",
          height: "100%",
          minWidth: `${COMPACT_NODE_SIZE}px`,
          minHeight: `${COMPACT_NODE_SIZE}px`,
          padding: "8px",
          boxSizing: "border-box",
          border: "1px solid var(--main-border-color)",
          borderLeft: `3px solid ${color}`,
          borderRadius: "8px",
          backgroundColor: "var(--content-item-bg)",
          color: "var(--main-font-color)",
          position: "relative",
        }}
      >
        <div
          className="node-drag-handle"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            width: "100%",
            cursor: "move",
          }}
        >
          <div style={{ fontSize: "12px", fontWeight: 700, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>
            {label}
          </div>
          <NodeIcon icon={def.icon} size={40} color={color} />
        </div>

        <button
          type="button"
          className="nodrag"
          onClick={() => data.onDelete?.(id)}
          aria-label="Delete node"
          title="Delete node"
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            width: "16px",
            height: "16px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid var(--main-border-color)",
            borderRadius: "4px",
            background: "transparent",
            color: "var(--main-font-color)",
            cursor: "pointer",
            fontSize: "12px",
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {staticTargets.map((handle, index) => (
          <Handle
            key={`t-${handle.id ?? "unnamed"}`}
            id={handle.id ?? undefined}
            type="target"
            position={Position.Left}
            style={{ ...handleBaseStyle, top: spreadTop(index, staticTargets.length) }}
          />
        ))}

        {sources.map((handle, index) => (
          <Handle
            key={`s-${handle.id ?? "unnamed"}`}
            id={handle.id ?? undefined}
            type="source"
            position={Position.Right}
            style={{ ...handleBaseStyle, top: spreadTop(index, sources.length) }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        minHeight: "80px",
        height: fixedHeight ? "100%" : "auto",
        padding: "10px",
        boxSizing: "border-box",
        border: "1px solid var(--main-border-color)",
        borderLeft: `3px solid ${color}`,
        borderRadius: "8px",
        backgroundColor: "var(--content-item-bg)",
        color: "var(--main-font-color)",
        position: "relative",
      }}
    >
      <NodeResizeControl style={{ zIndex: 40000, color: "var(--main-font-color)" }}>
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", right: 1, bottom: 1, opacity: 0.5 }}>
          <path d="M10 10 L10 5 L5 10 Z" fill="currentColor" />
        </svg>
      </NodeResizeControl>

      <div className="node-drag-handle" style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "move", minHeight: "20px" }}>
        <NodeIcon icon={def.icon} size={17} color={color} />
        <div style={{ fontSize: "13.5px", fontWeight: 700, flex: 1, wordBreak: "break-word", lineHeight: 1.25 }}>
          {label}
        </div>
        <span
          title={def.category}
          style={{
            padding: "1px 7px",
            fontSize: "9.5px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            borderRadius: "999px",
            color: "#fff",
            backgroundColor: color,
            whiteSpace: "nowrap",
          }}
        >
          {def.category ?? "node"}
        </span>
        <button
          type="button"
          className="nodrag"
          onClick={() => data.onDelete?.(id)}
          aria-label="Delete node"
          title="Delete node"
          style={{
            flexShrink: 0,
            width: "18px",
            height: "18px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid var(--main-border-color)",
            borderRadius: "4px",
            background: "transparent",
            color: "var(--main-font-color)",
            cursor: "pointer",
            fontSize: "13px",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {description ? (
        <div style={{ fontSize: "11px", lineHeight: 1.4, opacity: 0.85, maxHeight: "58px", overflow: "hidden" }}>
          {description}
        </div>
      ) : null}

      {hasVariableInputs ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#a0a6b1" }}>
            Inputs
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "12px", fontWeight: 600 }}>Count</span>
            <input
              className="nodrag"
              type="number"
              min={minInputs}
              step={1}
              value={inputsValue.length}
              onChange={(event) => {
                const count = Math.max(minInputs, Number(event.target.value) || 0);
                setInputCount(count);
              }}
              style={{ ...fieldStyle, width: 64, padding: "3px 6px" }}
            />
          </div>
          {inputsValue.map((input, index) => (
            <div key={input?.id ?? index} style={{ position: "relative", paddingLeft: "10px" }}>
              <input
                className="nodrag"
                type="text"
                value={input?.label ?? ""}
                placeholder={`Input ${index + 1}`}
                onChange={(event) => setInputLabel(index, event.target.value)}
                style={fieldStyle}
              />
              <Handle
                id={input?.id ?? `input-${index + 1}`}
                type="target"
                position={Position.Left}
                style={{ ...rowHandleStyle, left: "-14px" }}
              />
            </div>
          ))}
        </div>
      ) : null}

      {((!hasVariableInputs && targets.length > 0) || sources.length > 0) ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "11px", color: "#a0a6b1" }}>
          {!hasVariableInputs && targets.length > 0 && (
            <div><strong>In:</strong> {targets.map((handle) => handle.label).join(", ")}</div>
          )}
          {sources.length > 0 && (
            <div><strong>Out:</strong> {sources.map((handle) => handle.label).join(", ")}</div>
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minHeight: 0, overflowY: fixedHeight ? "auto" : "visible" }}>
        {basic.map((parameter, index) => (
          <ParameterField
            key={`${parameter?.name ?? "param"}-${index}`}
            parameter={parameter}
            value={getValue(parameter)}
            onChange={(value) => setValue(parameter?.name, value)}
            data={resolvedData}
            capabilities={capabilities}
            optionsEndpoint={optionsEndpoint}
          />
        ))}

        {advanced.length > 0 ? (
          <>
            <button
              type="button"
              className="nodrag"
              onClick={() => setShowAdvanced((value) => !value)}
              style={{
                alignSelf: "flex-start",
                padding: "2px 8px",
                fontSize: "11px",
                fontWeight: 600,
                border: "1px solid var(--main-border-color)",
                borderRadius: "999px",
                cursor: "pointer",
                color: "var(--main-font-color)",
                backgroundColor: "transparent",
              }}
            >
              {showAdvanced ? "Hide advanced" : `Advanced (${advanced.length})`}
            </button>
            {showAdvanced &&
              advanced.map((parameter, index) => (
                <ParameterField
                  key={`${parameter?.name ?? "param"}-${index}`}
                  parameter={parameter}
                  value={getValue(parameter)}
                  onChange={(value) => setValue(parameter?.name, value)}
                  data={resolvedData}
                  capabilities={capabilities}
                  optionsEndpoint={optionsEndpoint}
                />
              ))}
          </>
        ) : null}

        <NodeBuilderPanel
          definition={def}
          data={resolvedData}
          capabilities={capabilities}
          loading={capabilitiesLoading}
          error={capabilitiesError}
        />
      </div>

      {staticTargets.map((handle, index) => (
        <Handle
          key={`t-${handle.id ?? "unnamed"}`}
          id={handle.id ?? undefined}
          type="target"
          position={Position.Left}
          style={{ ...handleBaseStyle, top: spreadTop(index, staticTargets.length) }}
        />
      ))}

      {sources.map((handle, index) => (
        <Handle
          key={`s-${handle.id ?? "unnamed"}`}
          id={handle.id ?? undefined}
          type="source"
          position={Position.Right}
          style={{ ...handleBaseStyle, top: spreadTop(index, sources.length) }}
        />
      ))}
    </div>
  );
};


export default DynamicNode;
